import React, { useCallback, useMemo } from 'react'
import classnames from 'clsx'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import styled from 'styled-components'
import moment from 'moment'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineCheckIcon from 'components/Luxkit/Icons/line/LineCheckIcon'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import { getPackageViewPricingPerNight } from 'lib/offer/getPackageViewPricingPerNightUtils'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
`

const PackagePrices = styled(Caption)`
  display: flex;
  flex: 1;
`

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const TagWrapper = styled.div`
  display: flex;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    flex-direction: column;
  }
`

const StyledUpgradeButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

const BNBLBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
  padding: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const OptionsAvailableCaptionContainer = styled.div`
  margin-top: ${rem(8)};
  text-align: center;
`

interface Props {
  offer: App.Offer;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  isLoggedIn: boolean;
  checkIn?: string;
  currency: string;
  buyNowBookLater: boolean;
  room: App.RoomOccupants;
  isFlightsEnabled?: boolean;
  packageOption: OfferPackageOptionView;
  isFetching?: boolean;
  isFlightsSelected?: boolean;
  isUpgradable?: boolean;
  upgradeButtonText?: string;
  availablePackagesQuantity?: number;
  openRoomsModal: (room: App.RoomOccupants) => void;
  defaultDepositAmountPercentage: number;
  isPerNightPricingTestEnabled: boolean;
}

function MultipleRoomsBookingPriceDetails(props: Props) {
  const {
    offer,
    flightOrigin,
    flightDestination,
    checkIn,
    currency,
    buyNowBookLater,
    room,
    isFlightsEnabled,
    packageOption,
    isFetching,
    isFlightsSelected,
    isUpgradable,
    upgradeButtonText,
    availablePackagesQuantity = 0,
    openRoomsModal,
    defaultDepositAmountPercentage,
    isPerNightPricingTestEnabled,
  } = props

  const {
    package: pkg,
    flightPrice,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    propertyFees,
  } = useMemo(() => {
    return isPerNightPricingTestEnabled ? getPackageViewPricingPerNight(packageOption) : packageOption
  }, [packageOption, isPerNightPricingTestEnabled])

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const showIncludedGuestsExceeded = useMemo(() => hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlights && offer.bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = hotelDiscountPercent > bundledDiscountPercent ? hotelDiscountPercent : bundledDiscountPercent

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > 330
  }, [checkInMoment])

  const totalPrice = hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = useMemo(() => {
    if (hotelMemberPrice > 0) {
      return hotelMemberPrice + (propertyFees ?? 0)
    }
    return 0
  }, [hotelMemberPrice, propertyFees])
  const showMemberPrice = baseMemberPrice > 0

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const shouldShowValue = hotelValue > (showMemberPrice ? hotelMemberPrice : hotelPrice) && pkg.shouldDisplayValue
  const shouldShowBNBL = packageOption.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && buyNowBookLater
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, (showMemberPrice ? hotelMemberPrice : hotelPrice)) && !buyNowBookLater

  const handleChangePackage = useCallback(() => {
    openRoomsModal(room)
  }, [openRoomsModal, room])

  return (
    <PriceDetails>
      {shouldShowBNBL && (
        <BNBLBox>
          <LineCheckIcon size="XS" />
          <BodyText variant="small" weight="bold">Buy now, choose dates later</BodyText>
        </BNBLBox>
      )}
      <TagWrapper>
        {isDepositEnabled &&
          <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>
        }
      </TagWrapper>
      <PackagePrices variant="medium" className={classnames({ show_discount: shouldShowDiscount || isDepositEnabled })}>
        {!offer.bundledWithFlightsOnly && !isFlightsSelected && (
          <PackagePrice data-testid="pricing">
            <PriceRowPriceCaption>{pluralizeToString('night', pkg.duration)} {!hasFlights && 'from'}</PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="S"
              price={totalPrice}
              memberPrice={totalMemberPrice}
              saleUnit={isPerNightPricingTestEnabled ? 'night' : 'total'}
              loyaltyConfig={offer.luxPlus}
            />
            <PriceRowTaxesAndFees />
            {shouldShowValue && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="M"
              originalValue={hotelValue}
              discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
            />}
            {showIncludedGuestsExceeded && (
              <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />
            )}

          </PackagePrice>
        )}

        {isFlightsEnabled && isFlightsSelected && (
          <PackagePrice data-testid="flights-pricing">
            {!hasFlights && flightOrigin?.code === flightDestination?.code && (
              <ChangeDepartureWrapper>
                <PriceRowCaption>
                  No flights available from {flightOrigin?.name}. Please select another departing airport.
                </PriceRowCaption>
                <TextButton
                  kind="tertiary"
                  onClick={onToggleFilter}
                  data-filter="airport"
                >
                  Change airport
                </TextButton>
              </ChangeDepartureWrapper>
            )}
            {!hasFlights && !flightsBundledNoCache && flightOrigin?.code !== flightDestination?.code && !departureTooFarInFuture && (
              <>
                <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                  {pluralizeToString('night', pkg.duration)} + flights from
                </PriceRowPriceCaption>
                <LuxPlusPriceStack
                  size="S"
                  price={totalPrice}
                  saleUnit={isPerNightPricingTestEnabled ? 'night*' : 'total*'}
                  memberPrice={totalMemberPrice}
                  loyaltyConfig={offer.luxPlus}
                />
                <PriceRowTaxesAndFees />
              </>
            )}
            {!hasFlights && departureTooFarInFuture && (
              <ChangeDepartureWrapper>
                <PriceRowCaption>
                  Flights can only be booked up to 11 months in advance.
                </PriceRowCaption>
              </ChangeDepartureWrapper>
            )}
            {hasFlights && <>
              <PriceRowPriceCaption startIcon={<LinePlaneFlyIcon />}>
                {pluralizeToString('night', pkg.duration)} + flights from
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={totalPrice + flightPrice}
                memberPrice={totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0}
                saleUnit={isPerNightPricingTestEnabled ? 'night*' : 'total*'}
                loyaltyConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={hotelValue + flightPrice}
              />}
              {showIncludedGuestsExceeded && (
                <AdditionalGuestsPopup
                  complex={false}
                  description="+ Extra guest surcharge"
                  modalContent={pkg.roomPolicyDescription}
                />
              )}
            </>}
            <AtolProtectedTooltip />
          </PackagePrice>
        )}
        <LoadingIndicator visible={isFetching} floating opaque />
      </PackagePrices>
      {isUpgradable && <>
        <StyledUpgradeButton kind="secondary" fit="flex" onClick={handleChangePackage}>
          {upgradeButtonText ?? 'Upgrade room'}
        </StyledUpgradeButton>
        {availablePackagesQuantity > 0 &&
          <OptionsAvailableCaptionContainer>
            <PriceRowCaption>
              {pluralizeToString('option', availablePackagesQuantity)} available
            </PriceRowCaption>
          </OptionsAvailableCaptionContainer>
        }
      </>
      }
    </PriceDetails>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isPerNightPricingTestEnabled: !!getOptimizelyExperimentVariation(appState, OptimizelyExperiments.pricePerNight),
}))(MultipleRoomsBookingPriceDetails)
